import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: string;
  entity?: string;
  entityLabel?: string;
  where?: [string, ...string[]];
  whereNote?: string;
  figure?: ReactNode;
}[] = [
  {
    title: 'One place for the files you send again and again',
    body: 'The Document library holds the files your team keeps re-sending: proposals, decks, contracts, pricing sheets, case studies. Upload one once and everyone attaches the same current copy, instead of hunting through old threads for the right version. It is available whenever your workspace has the CRM or Support add-on turned on, and the files belong to your workspace alone.',
    where: ['Library', 'Documents'],
    whereNote:
      'In the sidebar under Workspace, Library groups Documents, Forms and Templates. If Documents is not listed there, neither CRM nor Support is turned on for your workspace.',
  },
  {
    title: 'Who can do what',
    body: 'Anyone who can use the CRM or Support can browse, search, download, and attach documents. Only workspace admins and owners can add a document, upload a new version, change its details, or archive it. Keeping the writes with admins means the files your customers receive are the ones your team agreed on. A teammate who is not an admin sees the same list without the upload and edit controls.',
  },
  {
    title: 'Add a document',
    body: 'Choose Upload document, pick a file, and give it a name. A description, a category (Proposal, Contract, Deck, Case study, Pricing, Guide, or Other) and tags are optional and make it easier to find later. A file can be up to 100 MB. PDFs, images (PNG, JPEG, GIF, WebP), Word, Excel and PowerPoint files, and plain text or CSV files are accepted. Formats that can run code in the viewer, such as SVG, HTML, and macro-enabled Office files, are not.',
    where: ['Library', 'Documents', 'Upload document'],
    whereNote:
      'The upload shows its progress. Large files take a moment longer at the end while Revvly checks them.',
  },
  {
    title: 'Every file is checked before anyone can use it',
    body: 'These files go to your customers, so Revvly does not take a file at its word. Once the upload finishes, it confirms that the contents really are the type the file claims to be, and, where malware scanning is set up for your deployment, scans it. A file that fails either check is refused with the reason and removed. It never appears in the library, and nobody can attach it. If the scanner is configured but temporarily unreachable, the upload waits and you can try again, rather than letting an unchecked file through.',
  },
  {
    title: 'Find and download',
    body: 'Search matches a document’s name, description, and file name. Narrow the list by category or tag. Download opens the current version through a link that expires after 30 minutes, so a copied link does not give anyone lasting access to the file.',
    where: ['Library', 'Documents'],
    whereNote: 'Download is on every row. The row shows the file type, size, and version number.',
  },
  {
    title: 'Keep a document current with versions',
    body: 'When the deck changes, upload a new version of the same document rather than adding a second one. The library serves the newest version from then on, so a template or teammate that already points at the document picks up the change without anyone re-linking it. Earlier versions are never overwritten or deleted; Version history lists every one, with the file name, size, an optional note on what changed, and the date, and each can still be downloaded.',
    where: ['Library', 'Documents', 'row menu', 'Upload new version'],
    whereNote:
      'Edit details changes the name, description, category, and tags without touching the file. Version history is in the same menu.',
  },
  {
    title: 'Archive instead of deleting',
    body: 'Archive a document you no longer want offered. It leaves the library list and every picker, so nobody attaches it by mistake, and a template that links to it simply skips it. Nothing is lost: admins can turn on Show archived and Restore it. Documents are archived rather than deleted so that the version history of something already sent stays intact.',
    where: ['Library', 'Documents', 'row menu', 'Archive'],
    whereNote: 'Show archived is an admin control at the top of the page.',
  },
  {
    title: 'Send a document in an email',
    body: 'In the composer, next to Attach, the Library button opens a searchable list of your documents. What happens when you pick one depends on its size. A file of 10 MB or less is attached to the email as a file, with a small Library tag on it, the same as a file from your computer. A file over 10 MB is too big for mail providers to carry, so it goes in as a link instead, placed in the body of the email where your cursor is. Either way you pick the document the same way and Revvly chooses for you; you can change your mind per document, as described below.',
    where: ['Clients', 'open a client', 'Conversation', 'Library'],
    whereNote:
      'The Library button appears on the Email channel when your workspace has CRM or Support. The usual email limits still apply to attachments: up to 5 files, 10 MB each, and 20 MB in total.',
    figure: (
      <Figure
        title="File or link, by size"
        caption="You pick the document the same way every time, and its size decides whether it travels as a file or a link."
      >
        <Flow
          steps={[
            { title: 'Pick from Library', text: 'In the email composer, next to Attach.' },
            {
              fork: [
                {
                  title: '10 MB or less',
                  text: 'Attached as a file, with a small Library tag.',
                  tone: 'accent',
                },
                {
                  title: 'Over 10 MB',
                  text: 'Goes in as a link in the body, where your cursor is.',
                  tone: 'warn',
                },
              ],
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'A large document becomes a link in your text',
    body: 'Click where you want the document mentioned, pick it from Library, and its name appears there as a link, so you can write the sentence around it, for example “Here is the full deck, and below it the pricing.” You do not have to rewrite anything when you send: Revvly swaps that link for a personal, tracked link to the document at the moment the email goes out. A document you already attached can be switched too: Send as link on its chip removes the attachment and puts the link in the text the same way, and Attach instead on a link chip switches a small file back to an attachment.',
    where: ['Clients', 'open a client', 'Conversation', 'Library'],
    whereNote: 'Up to five links per email. A link works for any file up to 100 MB.',
  },
  {
    title: 'Choose what the recipient can do',
    body: 'Each link has a chip under the editor. Pin this version freezes the link to the version that is current when you send, so it keeps showing that file even after you upload a new one; without it the link always shows the latest version. Preview only stops the recipient from downloading the file and lets them read it on the page. A link stops working after 180 days. Revvly gives every send its own link, so the same document sent to two people in two emails has two links, and a link that is forwarded on still points at the document you sent.',
  },
  {
    title: 'What the recipient sees',
    body: 'The link opens a plain page with your company name, the document name, and the file. PDFs and images preview right on the page; other files, such as Word, Excel and PowerPoint, show a Download button. If you turned download off there is no button. When an email went to several people the page asks who is viewing, so the view can be credited to the right person. A link that has expired, been revoked, or points at an archived document shows the same short message, whatever the reason.',
  },
  {
    title: 'Keep the link where you put it',
    body: 'If you delete the link from the text, the chip says “Not in the text, added at the end”: the email will still carry the link, under a Documents heading at the end of the message. Place at cursor puts it back in the text. Removing the chip removes the link from the text too, so a document you no longer want to send does not go out. A saved draft remembers the documents you chose as links, with their options, and brings the chips back when you reopen it; a document that has been archived since is removed and you are told.',
  },
  {
    title: 'Link documents to an email template',
    body: 'A template can carry up to five library documents. Applying the template to a draft fills in the subject and body and attaches the current version of each linked document, so the proposal always goes out with the deck your team meant to send. The template stores a reference to the document, not a copy of the file, which is why updating the document updates every template that links to it. A linked document that is too large to attach goes into the email as a link, at the end of the message, because a template has no place in your text for it. If a linked document has been archived or removed, the rest of the template still applies and one message lists what was left out.',
    where: ['Library', 'Templates', 'Email', 'a template', 'Attach library documents'],
    whereNote:
      'You can also tick the same option when you save the current draft as a new template from the composer.',
    entity: 'crmActivity',
    entityLabel: 'Activity timeline',
  },
  {
    title: 'See who opened it',
    body: 'Opening a link in a browser counts as a view, shown on the email as Document viewed. If the email itself never registered an open (many mail apps block the image Revvly uses to detect opens), a viewed document also marks the email as opened, so a client who read your proposal is not left showing Not opened. Mail security scanners often visit every link in a message; Revvly recognises them and does not count those visits. One recipient opening a link repeatedly inside 15 minutes counts once. On an email sent to several people, a view is credited to the email, and to a person when the link went to just one recipient or when they pick their own address on the page. Bcc recipients are never named on a link.',
    figure: (
      <Figure
        title="What counts as a view"
        caption="Only a person opening the link in a browser is counted, and repeat opens inside 15 minutes count once."
      >
        <Flow
          steps={[
            { title: 'Someone opens the link', text: 'In a browser, from the email you sent.' },
            {
              fork: [
                {
                  title: 'A mail security scanner',
                  text: 'Recognised and not counted.',
                  tone: 'muted',
                },
                {
                  title: 'A person',
                  text: 'Counts as a view. The email shows Document viewed.',
                  tone: 'accent',
                },
              ],
            },
            {
              title: 'Email open not registered?',
              text: 'A viewed document also marks the email as opened.',
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'Get told when it is opened',
    body: 'The first time a new person opens a link in a browser, the person who sent it gets a push notification naming the document and, when it is known, the email or ticket it was sent in. Tapping it opens that conversation. It goes only to the sender, only for the first few views of a link, and only where the sender has turned on push notifications in their account; a scanner visit never triggers one.',
  },
  {
    title: 'Links in a Support reply',
    body: 'In a public reply on a ticket, the Link a library document button adds a tracked link to the message, at the end, under Documents. The customer sees a plain link in the reply and the email; agents see under Documents sent on the ticket whether each one was opened. Links are not available on internal notes, and the ticket must exist before a link is made.',
    where: ['Support', 'a ticket', 'reply'],
  },
  {
    title: 'Track and revoke links',
    body: 'In Library, Documents, the Sent as links column shows how many times each document went out and how many of those links were opened. Links sent in the row menu lists every link: where it went, when, who it was addressed to, how often it was viewed, and whether it is still live. Revoke takes a link offline at once. Only the person who sent it or an admin can revoke a link. A link also goes offline on its own if the email that carried it fails to send. Archiving a document stops its links from opening, and restoring it brings them back.',
    where: ['Library', 'Documents', 'row menu', 'Links sent'],
  },
  {
    title: 'Limits and what it does not do yet',
    body: 'A document can be up to 100 MB, an email carries up to five links, and a link lasts 180 days. Links use the Revvly domain; using your own domain for links is planned. The notification when a link is opened is a browser push and has no setting of its own to mute it yet. Links in a Support reply are added at the end of the message rather than at a cursor position.',
  },
];

export function DocumentLibraryGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Reuse documents with the Document library — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Reuse documents</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Reuse your <em>documents</em>.
        </h1>
        <p className="dp-oneliner">
          Upload a proposal, deck, or contract once, keep it current with versions, and attach it to
          any email from the composer, send it as a tracked link, or link it to a template. Files up
          to 100 MB are type-checked, and malware-scanned where scanning is set up, before anyone
          can use them, and only admins can change what your team sends.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
          {step.figure}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link
                  to={`/docs/concepts/${step.entity}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
